import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  GridApi,
  GridReadyEvent,
  GridState,
  StateUpdatedEvent,
} from 'ag-grid-community';

/**
 * The slices of ag-grid's state that make up a list's *layout*: what the user
 * can change about how the columns are arranged, and nothing about what the
 * list shows or where they were in it.
 *
 * Deliberately excluded: `sort` and `filter` (a query, not a layout; our lists
 * fetch server-side and encode those in the URL where they belong),
 * `pagination` (paged from the URL), and the focus and selection slices
 * (restoring a stale selected row on a fresh load is disorienting).
 */
const LAYOUT_KEYS = [
  'columnSizing',
  'columnOrder',
  'columnPinning',
  'columnVisibility',
] as const satisfies readonly (keyof GridState)[];

type LayoutKey = (typeof LAYOUT_KEYS)[number];

/**
 * Bump when the persisted shape changes. Saved entries under an older version
 * are simply never read again.
 *
 * Not needed when a grid gains or loses a column: colIds a saved layout
 * mentions that no longer exist are ignored by ag-grid, and columns it doesn't
 * mention keep their column-definition defaults.
 */
const SCHEMA_VERSION = 1;

export function gridLayoutStorageKey(gridId: string): string {
  return `hawk:grid-layout:v${SCHEMA_VERSION}:${gridId}`;
}

function isStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.every(v => typeof v === 'string');
}

function hasColIds(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(
      entry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { colId?: unknown }).colId === 'string'
    )
  );
}

/**
 * Shape-check one persisted slice.
 *
 * localStorage is user-writable and outlives deploys, so a malformed entry must
 * never reach `initialState`: ag-grid throws on a bad state object rather than
 * ignoring it, which would take the whole page down instead of just losing the
 * saved layout. Each slice is checked on its own so one bad slice costs only
 * itself.
 */
function isValidSlice(key: LayoutKey, value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  switch (key) {
    case 'columnSizing':
      return hasColIds(v.columnSizingModel);
    case 'columnOrder':
      return isStringArray(v.orderedColIds);
    case 'columnPinning':
      return isStringArray(v.leftColIds) && isStringArray(v.rightColIds);
    case 'columnVisibility':
      return isStringArray(v.hiddenColIds);
  }
}

function parseLayout(raw: string): GridState | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const source = parsed as Record<string, unknown>;

  const layout: GridState = {};
  let any = false;
  for (const key of LAYOUT_KEYS) {
    const slice = source[key];
    if (slice === undefined || !isValidSlice(key, slice)) continue;
    // The validator above proved the shape; this is just the narrowing.
    (layout as Record<string, unknown>)[key] = slice;
    any = true;
  }
  return any ? layout : null;
}

/** Narrow a full grid state to the layout slices, in a stable key order. */
export function pickLayout(
  state: GridState,
  appColIds: readonly string[] = []
): GridState {
  const picked: GridState = {};
  for (const key of LAYOUT_KEYS) {
    const slice = state[key];
    if (slice === undefined) continue;
    (picked as Record<string, unknown>)[key] = slice;
  }
  if (picked.columnVisibility && appColIds.length) {
    const hiddenColIds = picked.columnVisibility.hiddenColIds.filter(
      id => !appColIds.includes(id)
    );
    if (hiddenColIds.length) picked.columnVisibility = { hiddenColIds };
    else delete picked.columnVisibility;
  }
  return picked;
}

/**
 * Latest known layout per grid, held outside React.
 *
 * ag-grid reads `initialState` once per grid *mount*, and our lists unmount
 * the grid whenever they swap in a skeleton or an empty state (a search that
 * matches nothing), so the layout has to be served from somewhere that
 * outlives the component: a module-level read-through cache of localStorage,
 * which also keeps two mounts of the same grid in agreement.
 *
 * Absent key means "not read yet"; `null` means "nothing stored".
 */
const layoutCache = new Map<string, GridState | null>();

/**
 * Serialized *default* layout per grid: what the grid reports when its columns
 * match the column definitions.
 *
 * ag-grid always reports `columnSizing` and `columnOrder`, so a state update
 * carrying them says nothing about whether the user changed anything. The
 * baseline is what tells a pristine layout from a customized one, so that a
 * layout dragged back to the defaults (or reset) is forgotten rather than
 * saved, and the reset control disappears with it.
 */
const defaultsCache = new Map<string, string>();

/** Test seam: drop all cached state so cases don't leak into each other. */
export function __clearGridLayoutCaches(): void {
  layoutCache.clear();
  defaultsCache.clear();
}

function cachedLayout(gridId: string): GridState | null {
  const hit = layoutCache.get(gridId);
  if (hit !== undefined) return hit;

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(gridLayoutStorageKey(gridId));
  } catch {
    // localStorage can be unavailable (private mode / sandboxed iframe).
  }
  const layout = raw === null ? null : parseLayout(raw);
  layoutCache.set(gridId, layout);
  return layout;
}

function forgetLayout(gridId: string): void {
  layoutCache.set(gridId, null);
  try {
    localStorage.removeItem(gridLayoutStorageKey(gridId));
  } catch {
    // Storage unavailable; the in-memory cache above is still cleared.
  }
}

function saveLayout(gridId: string, layout: GridState, serialized: string) {
  layoutCache.set(gridId, layout);
  try {
    localStorage.setItem(gridLayoutStorageKey(gridId), serialized);
  } catch {
    // Quota exceeded or storage unavailable: a saved layout is not worth
    // failing a render over, and the in-memory cache still covers remounts.
  }
}

/** The `AgGridReact` props that wire a grid up to its persisted layout. */
export interface GridLayoutProps {
  initialState: GridState | undefined;
  onGridReady: (event: GridReadyEvent) => void;
  onStateUpdated: (event: StateUpdatedEvent) => void;
}

export interface GridLayout {
  /**
   * Spread onto `AgGridReact`. A grid that has its own `onGridReady` should
   * spread these first and chain `gridProps.onGridReady` from its handler.
   */
  gridProps: GridLayoutProps;
  /** Restore the column definitions' layout and forget the saved one. */
  reset: () => void;
  /** True while a saved layout is in effect, so callers can show `reset`. */
  isCustomized: boolean;
}

const NO_APP_COLUMNS: Record<string, boolean> = {};

function applyAppColumns(api: GridApi, columns: Record<string, boolean>) {
  const ids = Object.keys(columns);
  const shown = ids.filter(id => columns[id]);
  const hidden = ids.filter(id => !columns[id]);
  if (shown.length) api.setColumnsVisible(shown, true);
  if (hidden.length) api.setColumnsVisible(hidden, false);
}

/**
 * Persist a grid's column layout (widths, order, pinning, visibility) in
 * localStorage, so it survives reloads and return visits.
 *
 * `gridId` names the storage entry and must be stable for the grid's lifetime
 * and unique across the app (one per list page). `appColumns` maps column ids
 * to visibility the app decides; it is applied on mount, on change and after a
 * reset, and never saved.
 */
export function useGridLayout(
  gridId: string,
  appColumns: Record<string, boolean> = NO_APP_COLUMNS
): GridLayout {
  const apiRef = useRef<GridApi | null>(null);
  const appColumnsRef = useRef(appColumns);
  const [isCustomized, setIsCustomized] = useState(
    () => cachedLayout(gridId) !== null
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      apiRef.current = event.api;
      // With nothing saved the grid is sitting at its column definitions, so
      // this is a free reading of the baseline. With a saved layout applied
      // it is not, and the baseline is learnt from the first reset instead.
      if (!defaultsCache.has(gridId) && cachedLayout(gridId) === null) {
        defaultsCache.set(
          gridId,
          JSON.stringify(
            pickLayout(event.api.getState(), Object.keys(appColumnsRef.current))
          )
        );
      }
      applyAppColumns(event.api, appColumnsRef.current);
    },
    [gridId]
  );

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      // 'gridInitializing' also lands here and would otherwise overwrite a
      // saved entry with the column definitions' defaults on every mount.
      const relevant = event.sources.some(source =>
        (LAYOUT_KEYS as readonly string[]).includes(source)
      );
      if (!relevant) return;

      const layout = pickLayout(
        event.state,
        Object.keys(appColumnsRef.current)
      );
      const serialized = JSON.stringify(layout);
      if (defaultsCache.get(gridId) === serialized) {
        // Back at the defaults: nothing to restore next time, and nothing
        // for the reset control to undo.
        forgetLayout(gridId);
        setIsCustomized(false);
        return;
      }
      saveLayout(gridId, layout, serialized);
      // Setting an unchanged boolean is a no-op for React, so a drag that
      // fires this repeatedly does not re-render the list per frame.
      setIsCustomized(true);
    },
    [gridId]
  );

  const reset = useCallback(() => {
    const api = apiRef.current;
    if (api) {
      // Widths, order, pinning and visibility in one call. Column state is
      // recomputed synchronously (only the `stateUpdated` event is debounced),
      // so what the grid reports now *is* the baseline, and the debounced echo
      // of this reset is then recognized as the defaults rather than re-saved.
      api.resetColumnState();
      defaultsCache.set(
        gridId,
        JSON.stringify(
          pickLayout(api.getState(), Object.keys(appColumnsRef.current))
        )
      );
      applyAppColumns(api, appColumnsRef.current);
    }
    forgetLayout(gridId);
    setIsCustomized(false);
  }, [gridId]);

  useEffect(() => {
    appColumnsRef.current = appColumns;
    if (apiRef.current) applyAppColumns(apiRef.current, appColumns);
  }, [appColumns]);

  return {
    // A fresh object each render, on purpose: the cached layout changes
    // outside React (in onStateUpdated), and a grid that remounts must read
    // the latest one. The handlers themselves are stable.
    gridProps: {
      initialState: cachedLayout(gridId) ?? undefined,
      onGridReady,
      onStateUpdated,
    },
    reset,
    isCustomized,
  };
}
